import { createHeaders } from './apiClient';
import { handleResponse } from './httpResponse';
/**
 * Connectors API Service (Track 3 S4)
 *
 * Handles the connector-management surface:
 * - List connectors (registry × per-tenant connection + credential + health)
 * - OAuth authorize (popup flow — the backend returns the IdP URL)
 * - Paste-key connect (API-key connectors / OAuth fallback)
 * - Disconnect
 */

// =============================================================================
// Types
// =============================================================================

export interface ConnectionSummary {
  connection_id: string;
  status: string;
  is_enabled: boolean;
  external_account_id: string | null;
  last_sync_at: string | null;
  last_sync_status: string | null;
}

export interface ConnectorSummary {
  connector_key: string;
  display_name: string;
  auth_kind: 'oauth2' | 'api_key' | 'none';
  supported_read: 'airbyte' | 'direct' | 'none';
  oauth_available: boolean;
  is_connected: boolean;
  credential_status: string | null;
  connections: ConnectionSummary[];
  health_status: string | null;
}

export interface ConnectorsResponse {
  connectors: ConnectorSummary[];
}

export interface ConnectResult {
  connector_key: string;
  connection_id: string;
  external_account_id: string;
  status: string;
}

// =============================================================================
// Configuration & helpers
// =============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// =============================================================================
// API functions
// =============================================================================

export async function listConnectors(): Promise<ConnectorsResponse> {
  const response = await fetch(`${API_BASE_URL}/api/connectors`, {
    method: 'GET',
    headers: createHeaders(),
  });
  return handleResponse<ConnectorsResponse>(response);
}

/**
 * Start the OAuth popup flow: the AUTHENTICATED authorize XHR returns the
 * IdP URL; the caller opens it in a popup (plain redirects are frame-blocked
 * in the embedded context, and a top-level redirect can't carry the Bearer
 * token). The popup completes at the backend callback, which postMessages
 * {type: 'oauth_result', ok} back to the opener.
 */
export async function getAuthorizeUrl(connectorKey: string): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/oauth/authorize`, {
    method: 'POST',
    headers: createHeaders(),
    body: JSON.stringify({ connector_key: connectorKey }),
  });
  const data = await handleResponse<{ authorize_url: string }>(response);
  return data.authorize_url;
}

export async function connectWithKey(
  connectorKey: string,
  credentials: Record<string, string>
): Promise<ConnectResult> {
  const response = await fetch(
    `${API_BASE_URL}/api/connectors/${connectorKey}/connect`,
    {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify({ credentials }),
    }
  );
  return handleResponse<ConnectResult>(response);
}

export async function disconnectConnector(
  connectorKey: string,
  connectionId: string
): Promise<{ disconnected: boolean }> {
  const response = await fetch(
    `${API_BASE_URL}/api/connectors/${connectorKey}/disconnect/${connectionId}`,
    {
      method: 'DELETE',
      headers: createHeaders(),
    }
  );
  return handleResponse<{ disconnected: boolean }>(response);
}
